/** @format */

import {forwardRef} from 'react';

import {TextField} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {
  ActionsContainer,
  FilterBarContainer,
  FilterContainer,
  FilterLabel,
  FiltersContainer,
  ResetButton,
  StyledAutocomplete,
} from './styles';
import {FilterOption, FilterBarProps} from './types';

export const FilterBar = forwardRef<HTMLDivElement, FilterBarProps>(
  ({filters, filterOptions, onFilterChange, onResetFilters, columns, actions}, ref) => {
    const {t} = useTranslation();

    const filterableColumns = columns.filter(col => col.enableFilter);

    if (filterableColumns.length === 0) {
      return null;
    }

    const hasActiveFilters = Object.values(filters).some(value => value !== '');

    const handleFilterChange =
      (columnId: string) => (_event: any, newValue: FilterOption | null) => {
        onFilterChange(columnId, newValue?.value || '');
      };

    const handleOptionEqual = (option: FilterOption, value: FilterOption) => {
      return option.value === value.value;
    };

    const renderTextInput = (params: any) => (
      <TextField {...params} placeholder={t('Все')} variant='outlined' />
    );

    const renderFilterOption = (props: any, option: FilterOption) => {
      return (
        <li {...props} style={{fontSize: '14px'}}>
          {option.label}
        </li>
      );
    };

    return (
      <FilterBarContainer ref={ref}>
        <FiltersContainer>
          {filterableColumns.map(({id, title}) => {
            const autocompleteOptions = filterOptions[id] || [];

            const currentValue = autocompleteOptions.find(
              option => option.value === (filters[id] || ''),
            );

            return (
              <FilterContainer key={id}>
                <FilterLabel>{title}</FilterLabel>
                <StyledAutocomplete
                  size='small'
                  options={autocompleteOptions}
                  getOptionLabel={(option: FilterOption) => option.label}
                  value={currentValue || null}
                  onChange={handleFilterChange(id)}
                  isOptionEqualToValue={handleOptionEqual}
                  renderInput={renderTextInput}
                  renderOption={renderFilterOption}
                  noOptionsText={t('Ничего не найдено')}
                  clearText={t('Очистить')}
                  openText={t('Развернуть')}
                  closeText={t('Свернуть')}
                />
              </FilterContainer>
            );
          })}
        </FiltersContainer>

        <ActionsContainer>
          <ResetButton
            variant='outlined'
            size='small'
            onClick={onResetFilters}
            disabled={!hasActiveFilters}
          >
            {t('Сбросить фильтры')}
          </ResetButton>
          {actions}
        </ActionsContainer>
      </FilterBarContainer>
    );
  },
);

FilterBar.displayName = 'FilterBar';
